import React, { useRef, useEffect, useState } from 'react';
import Layout from 'components/Common/Layout';
import { v4 as uuidv4 } from 'uuid';
import Select from 'react-select';
import useTranslation from 'hooks/useTranslation';
import { ReactComponent as BackArrow } from 'assets/images/back.svg';
import { useHistory } from 'react-router-dom';
import DropFileContainer from 'components/Common/DropFileContainer';
import { ReactComponent as CubicIcon } from 'assets/images/cubic.svg';
import { useToastContext } from 'auto-design-common';
import { uploadFile, createGenAiImage, getGenAIRoomTypes, getGenAIStyles } from 'utils/api';
import tempate1 from 'assets/images/create-gen-ai-image/template1.png';
import tempate2 from 'assets/images/create-gen-ai-image/template2.png';
import tempate3 from 'assets/images/create-gen-ai-image/template3.png';
import tempate4 from 'assets/images/create-gen-ai-image/template4.png';
import { useQuery } from '@tanstack/react-query';
import makeAnimated from 'react-select/animated';
import { ReactComponent as MagicIcon } from 'assets/images/magic.svg';
import { getBase64Strings } from 'exif-rotate-js';
import { ReactComponent as Close } from 'assets/images/create-floor-plan/close-button.svg';
import { useAppContext } from 'components/AppContext';
import { ModalKey } from 'constants/modal';
import { useModalContext } from 'components/ModalContext';
import { ReactComponent as CoinIcon } from 'assets/images/Auto-Coin.svg';
import styles from './CreateGenAIImage.module.scss';

const animatedComponents = makeAnimated();

export default function CreateGenAIImage() {
  const { tCreateGenAIImage, tGenAIRoomTypes, tGenAIStyles, tError } = useTranslation();
  const { user, configs, getInfo } = useAppContext();
  const { showModal } = useModalContext();
  const history = useHistory();
  const inputRef = useRef(null);
  const { toastError } = useToastContext();
  const roomTypeSelectRef = useRef(null);
  const styleSelectRef = useRef(null);
  const genEmptyRoomSelectRef = useRef(null);
  const { data: roomTypes } = useQuery({
    queryKey: ['GenAIImageRoomTypes'],
    queryFn: () => getGenAIRoomTypes(),
  });

  const { data: roomStyles } = useQuery({
    queryKey: ['GenAIImageStyles'],
    queryFn: () => getGenAIStyles(),
  });
  const [file, setFile] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  const generate = async (file) => {
    if (user.remainingFreeGenAiImages <= 0 && user.balance < configs.gen_ai_image_price) {
      showModal(ModalKey.TOP_UP, {
        title: tCreateGenAIImage('youDonHaveEnoughCoins'),
        subtitle: (
          <div className={styles.feelFreeToAddMore}>
            {tCreateGenAIImage('feelFreeToAddMore')}
          </div>
        ),
      });
      return;
    }

    try {
      const { key } = await uploadFile(file, true);

      const res = await createGenAiImage({
        fileKey: key,
        roomType: roomTypeSelectRef.current.getValue()[0].value,
        style: styleSelectRef.current.getValue()[0].value,
        genEmptyRoom: genEmptyRoomSelectRef.current.getValue()[0].value,
      });

      history.push(`/gen-ai-images/${res.id}`);
    } catch (error) {
      console.error('Error generating image:', error);
      toastError(error);
    } finally {
      getInfo();
    }
  };

  const selectTemplate = async (templateUrl) => {
    try {
      setSubmitting(true);

      // Download the file from the template URL
      const response = await fetch(templateUrl);
      if (!response.ok) {
        throw new Error(`Failed to download template: ${response.statusText}`);
      }

      // Get the blob data
      const blob = await response.blob();

      // Extract filename from URL or use a default name
      const urlParts = templateUrl.split('/');
      const filename = urlParts[urlParts.length - 1] || 'template.png';

      // Create a File object from the blob
      const templateFile = new File([blob], filename, { type: blob.type });

      // Convert to base64 for thumbnail display
      const [data] = await getBase64Strings([templateFile], {
        maxSize: 20000,
        quality: 1,
      });

      // Set the template as the selected file
      setFile({
        id: uuidv4(),
        file: templateFile,
        thumbnailData: data,
      });
    } catch (error) {
      console.error('Error selecting template:', error);
      toastError(error);
    } finally {
      setSubmitting(false);
    }
  };

  const handleFileChange = async (files) => {
    const file = files[0];

    if (file.size > 200000000) {
      toastError(tError('fileTooLarge200MB'));
    }

    if (['image/png', 'image/jpeg'].includes(file.type)) {
      const [data] = await getBase64Strings([file], {
        maxSize: 20000,
        quality: 1,
      });

      setFile({
        id: uuidv4(),
        file,
        thumbnailData: data,
      });
    }
  };

  useEffect(() => {
    if (roomTypes?.[0]) {
      roomTypeSelectRef.current.setValue({
        value: roomTypes[0],
        label: tGenAIRoomTypes(roomTypes[0]),
      });
    }

    if (roomStyles?.[0]) {
      styleSelectRef.current.setValue({
        value: roomStyles[0],
        label: tGenAIStyles(roomStyles[0]),
      });
    }
  }, [roomTypes, roomStyles, tGenAIRoomTypes, tGenAIStyles]);

  return (
    <Layout>
      <div className={styles.header}>
        <button
          type="button"
          className={styles.backButton}
          onClick={() => {
            history.replace('/services');
          }}
        >
          <BackArrow />
        </button>
        <div className={styles.title}>
          {tCreateGenAIImage('title')}
        </div>
      </div>
      <div className={styles.content}>
        <input
          hidden
          ref={inputRef}
          type="file"
          className={styles.fileInput}
          accept="image/png, image/jpeg"
          onChange={(e) => {
            handleFileChange(e.target.files);
            e.target.value = '';
          }}
        />
        {!file && (
        <div className={styles.inner}>
          <DropFileContainer
            onDropFile={async (files) => {
              handleFileChange(files);
            }}
          >
            {() => (
              <div className={styles.dropArea}>
                <CubicIcon />

                <div className={styles.instruction1}>
                  {tCreateGenAIImage('dragYourRoomHere')}
                </div>
                <div className={styles.instruction2}>
                  {tCreateGenAIImage('or')}
                  <button
                    type="button"
                    className={styles.browseButton}
                    onClick={() => {
                      inputRef.current?.click();
                    }}
                  >
                    {tCreateGenAIImage('browse')}
                  </button>
                </div>
              </div>
            )}
          </DropFileContainer>

          <div className={styles.separatorContainer}>
            <div className={styles.separator} />
            <span>{tCreateGenAIImage('orTryOurTemplates')}</span>
            <div className={styles.separator} />
          </div>
          <div className={styles.templates}>
            <button type="button" className={styles.template} onClick={() => selectTemplate(tempate1)}>
              <img src={tempate1} alt="template1" />
            </button>
            <button type="button" className={styles.template} onClick={() => selectTemplate(tempate2)}>
              <img src={tempate2} alt="template2" />
            </button>
            <button type="button" className={styles.template} onClick={() => selectTemplate(tempate3)}>
              <img src={tempate3} alt="template3" />
            </button>
            <button type="button" className={styles.template} onClick={() => selectTemplate(tempate4)}>
              <img src={tempate4} alt="template4" />
            </button>
          </div>
        </div>
        )}

        {file && (
          <div className={styles.selectedFile}>
            <div className={styles.selectedFileImageWrapper}>
              <img src={file.thumbnailData} alt="file" className={styles.selectedFileImage} />
              <button
                type="button"
                className={styles.closeButton}
                onClick={() => {
                  setFile(null);
                }}
              >
                <Close />
              </button>
            </div>
          </div>
        )}

      </div>

      <div className={styles.footer}>
        <div className={styles.left}>
          <div className={styles.title}>
            {tCreateGenAIImage('tellUsAboutYourIdea')}
          </div>
          <div className={styles.selections}>
            <span>
              {tCreateGenAIImage('thisIsAPhotoOfARoom')}
            </span>
            <Select
              classNames={{
                control: () => styles.select,
              }}
              ref={roomTypeSelectRef}
              placeholder={tCreateGenAIImage('pickARoom')}
              components={animatedComponents}
              defaultValue={null}
              options={roomTypes?.map(type => ({
                value: type,
                label: tGenAIRoomTypes(type),
              })) || []}
            />
            <span>
              {tCreateGenAIImage('in')}
            </span>
            <Select
              classNames={{
                control: () => styles.select,
              }}
              ref={styleSelectRef}
              placeholder={tCreateGenAIImage('pickAStyle')}
              components={animatedComponents}
              defaultValue={null}
              options={roomStyles?.map(style => ({
                value: style,
                label: tGenAIStyles(style),
              })) || []}
            />
            <span>
              {'. '}
              {tCreateGenAIImage('iWantTo')}
            </span>
            <Select
              classNames={{
                control: () => styles.genEmptyRoomSelect,
              }}
              className={styles.select}
              ref={genEmptyRoomSelectRef}
              defaultValue={{ value: true, label: tCreateGenAIImage('remove') }}
              components={animatedComponents}
              options={[{ value: true, label: tCreateGenAIImage('remove') }, { value: false, label: tCreateGenAIImage('keep') }]}
            />
            <span>
              {tCreateGenAIImage('exisitngFurniture')}
            </span>
          </div>
        </div>
        <div>
          {user.remainingFreeGenAiImages > 0 && (
            <div className={styles.remainingFreeGenAiImages}>
              {tCreateGenAIImage('freeGenerationsLeft', { count: user.remainingFreeGenAiImages })}
            </div>
          )}
          <button
            type="button"
            className={styles.generateButton}
            onClick={async () => {
              setSubmitting(true);
              await generate(file.file);
              setSubmitting(false);
            }}
            disabled={!file || submitting}
          >
            <MagicIcon />
            {submitting ? tCreateGenAIImage('generating') : tCreateGenAIImage('generate')}
            {user.remainingFreeGenAiImages <= 0 && (
              <div className={styles.price}>
                {configs.gen_ai_image_price}
                {' x'}
                <CoinIcon />
                {tCreateGenAIImage('forGenerations', {
                  count: configs.free_gen_ai_images_per_coin * configs.gen_ai_image_price,
                })}
              </div>
            )}
          </button>
        </div>
      </div>
    </Layout>
  );
}
